import React, { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  completeOnboardingStep,
  completeOnboardingTour,
  setOnboardingTourStep,
  skipOnboardingTour,
} from '../../store/slices/helpSlice';
import { ONBOARDING_STEPS } from '../../lib/help/onboardingSteps';
import { trackHelpEvent } from '../../lib/help/helpAnalytics';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';

const OnboardingTourOverlay: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const tourState = useAppSelector((state) => state.help.tourState);
  const cardRef = useRef<HTMLDivElement>(null);
  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const isRunning = tourState.isRunning;
  const step = ONBOARDING_STEPS[tourState.currentStepIndex];
  const isLast = tourState.currentStepIndex >= ONBOARDING_STEPS.length - 1;

  const handleSkip = useCallback(() => {
    dispatch(skipOnboardingTour());
    trackHelpEvent('onboarding_skipped', { source: 'tour_overlay' });
  }, [dispatch]);

  const handleAdvance = useCallback(() => {
    if (!step) return;
    dispatch(completeOnboardingStep(step.id));
    trackHelpEvent('onboarding_step_completed', { source: 'tour_overlay', stepId: step.id });
    if (isLast) {
      dispatch(completeOnboardingTour());
      trackHelpEvent('onboarding_completed', { source: 'tour_overlay' });
    } else {
      dispatch(setOnboardingTourStep(tourState.currentStepIndex + 1));
    }
  }, [dispatch, isLast, step, tourState.currentStepIndex]);

  useEffect(() => {
    if (!isRunning) return;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    skipButtonRef.current?.focus();

    return () => {
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) prev.focus();
      previousFocusRef.current = null;
    };
  }, [isRunning]);

  useEffect(() => {
    if (!isRunning) return;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleSkip();
        return;
      }
      if (event.key !== 'Tab') return;

      const card = cardRef.current;
      if (!card) return;
      const focusables = card.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && card.contains(active);

      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isRunning, handleSkip]);

  if (!isRunning || !step) return null;

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-40 bg-[rgba(20,42,70,0.56)]"
      aria-live="polite"
      aria-label={t('help.guidedOnboarding', { defaultValue: 'Guided onboarding' })}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-tour-title"
        aria-describedby="onboarding-tour-desc"
        className="pointer-events-auto absolute left-1/2 top-4 w-[min(560px,calc(100vw-1.5rem))] -translate-x-1/2 rounded-xl border border-[rgba(176,210,255,0.24)] bg-[rgba(34,76,138,0.95)] p-4 shadow-[0_22px_48px_rgba(7,16,34,0.58)]"
      >
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-accent-primary">
          {t('help.guidedOnboarding', { defaultValue: 'Guided onboarding' })}
        </p>
        <h3 id="onboarding-tour-title" className="mb-1 text-sm font-semibold text-text-primary">
          {t(`help.steps.${step.id}.title`)}
        </h3>
        <p id="onboarding-tour-desc" className="mb-3 text-xs text-text-secondary">
          {t(`help.steps.${step.id}.description`)}
        </p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-text-muted">
            {t('help.stepLabel', {
              current: tourState.currentStepIndex + 1,
              total: ONBOARDING_STEPS.length,
              defaultValue: `Step ${tourState.currentStepIndex + 1} of ${ONBOARDING_STEPS.length}`,
            })}
          </span>
          <div className="flex gap-2">
            <Button
              ref={skipButtonRef}
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSkip}
            >
              {t('help.skip', { defaultValue: 'Skip' })}
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={handleAdvance}>
              {isLast
                ? t('help.done', { defaultValue: 'Done' })
                : t('help.next', { defaultValue: 'Next' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTourOverlay;
